import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const Check = ({ id, checked, onChange, children }) => (
  <label className="flex cursor-pointer items-start gap-3">
    <input type="checkbox" data-testid={id} checked={checked} onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-5 w-5 shrink-0 accent-[#E05A47]" />
    <span>{children}</span>
  </label>
);

const Field = ({ label, id, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-bold">{label}</span>
    <input data-testid={id} {...props}
      className="w-full rounded-2xl border-2 border-[color:var(--line)] bg-white px-4 py-3 text-lg outline-none transition-colors focus:border-[color:var(--terracotta)]" />
  </label>
);

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", child_name: "", child_age: 6, parental_consent: false, terms_accepted: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/bibliotheque" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ ...form, child_age: Number(form.child_age) });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto grid max-w-5xl gap-10 px-4 py-12 sm:px-8 md:grid-cols-2 md:py-20">
      <div className="rise">
        <h1 className="font-display text-5xl font-bold leading-tight">Espace Parents</h1>
        <p className="mt-4 text-lg text-[color:var(--muted-ink)]">Créez un compte pour suivre les progrès de votre enfant : histoires lues, mots découverts, badges gagnés.</p>
        <div className="mt-8 flex gap-2 rounded-full bg-[color:var(--paper-2)] p-1.5 w-fit">
          {[["login", "Connexion"], ["register", "Inscription"]].map(([m, l]) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
              className={`rounded-full px-5 py-2 font-display font-semibold transition-colors ${mode === m ? "bg-[color:var(--espresso)] text-white" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      <form onSubmit={submit} data-testid="auth-form" className="rise card-paper space-y-4 p-6 sm:p-8" style={{ animationDelay: "100ms" }}>
        {mode === "register" && (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Field label="Prénom ou surnom de l'enfant" id="auth-child-name-input" maxLength={30} value={form.child_name} onChange={set("child_name")} required /></div>
            <Field label="Âge" id="auth-child-age-input" type="number" min={2} max={12} value={form.child_age} onChange={set("child_age")} required />
          </div>
        )}
        <Field label="Email du parent" id="auth-email-input" type="email" value={form.email} onChange={set("email")} required />
        <Field label="Mot de passe" id="auth-password-input" type="password" minLength={6} value={form.password} onChange={set("password")} required />
        {mode === "login" && (
          <Link to="/mot-de-passe-oublie" data-testid="auth-forgot-password-link" className="block text-right text-sm font-bold text-[color:var(--terracotta)] underline">Mot de passe oublié ?</Link>
        )}
        {mode === "register" && (
          <div className="space-y-3 rounded-2xl bg-[color:var(--paper-2)] p-4 text-sm">
            <Check id="auth-parental-consent-checkbox" checked={form.parental_consent} onChange={(v) => setForm({ ...form, parental_consent: v })}>
              Je suis le parent ou le tuteur légal de l'enfant, j'ai plus de 18 ans et je consens à l'utilisation de son prénom et de son âge pour adapter les histoires.
            </Check>
            <Check id="auth-terms-checkbox" checked={form.terms_accepted} onChange={(v) => setForm({ ...form, terms_accepted: v })}>
              J'accepte les <Link to="/cgu-cgv" target="_blank" className="font-bold underline">CGU</Link> et j'ai lu la <Link to="/confidentialite" target="_blank" className="font-bold underline">Politique de confidentialité</Link>.
            </Check>
            <p className="text-xs text-[color:var(--muted-ink)]">Seul le prénom ou un surnom est demandé : pas de nom de famille, pas de photo. Aucune publicité.</p>
          </div>
        )}
        {error && <p data-testid="auth-error" className="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700">{error}</p>}
        <button type="submit" disabled={busy || (mode === "register" && !(form.parental_consent && form.terms_accepted))} data-testid="auth-submit-button" className="btn-primary w-full">
          {busy ? "Un instant…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
        </button>
      </form>
    </main>
  );
}
